import { Outlet, NavLink, useNavigate, Link } from 'react-router-dom'
import { Home, LogOut, History, SlidersHorizontal } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { useIsManager } from '../hooks/useIsManager'
import { cn } from '../lib/utils'
import ConnectionStatus, { OfflineBanner } from '../components/ConnectionStatus'

// Bottom nav — the few destinations worth reaching from ANY screen, not
// just from Home. History earns a slot because it's where you go to check
// or undo something you just did, from wherever you happen to be. Every
// other action (Sale, Collect, Purchase, Return, Quotation, stock…) is a
// Home tile, one thumb-reach away, so duplicating it here would waste a
// slot and clutter the bar.
const bottomNav = [
  { to: '/field', icon: Home, label: 'Home', end: true },
  { to: '/field/history', icon: History, label: 'History' },
]

// The hamburger sidebar that used to duplicate every screen is gone: Home's
// tiles cover them all, and Logout moved into the header, which is where it
// now lives.

export default function FieldLayout() {
  const { user, tenant, logout } = useAuth()
  const navigate = useNavigate()
  const isManager = useIsManager()

  // The Manage hub (the back office) is the last bottom-nav slot — for the
  // MANAGER only. It holds the profit report, the VAT return, every van's
  // takings, staff, and the company details printed on every invoice. A
  // driver has no business in any of it, and until now every account saw it.
  const bottomNavItems = isManager
    ? [...bottomNav, { to: '/field/manage', icon: SlidersHorizontal, label: 'Manage' }]
    : bottomNav
  const handleLogout = async () => {
    const loggedOut = await logout()
    if (loggedOut) navigate('/login')
  }

  return (
    // .app-shell (index.css) sizes this to the DYNAMIC viewport height, so
    // the bottom nav sits just above the browser's toolbar instead of
    // underneath it. It replaces `h-screen h-[100dvh]`: equal specificity
    // meant the later-emitted .h-screen (100vh) always won, which is why the
    // nav only appeared in fullscreen. See the class for the support ladder.
    <div className="app-shell bg-gray-50 flex flex-col overflow-hidden">
      {/* Header — fixed to top */}
      <header className="flex-shrink-0 bg-primary-600 text-white px-4 py-3 flex items-center justify-between safe-area-inset-top z-10">
        <Link to="/field" className="block min-w-0 hover:opacity-90 transition-opacity" aria-label="Home">
          <h1 className="font-semibold text-lg truncate">{tenant?.name || 'Van Sales'}</h1>
          <p className="text-sm text-primary-100 truncate">
            {user?.name}
          </p>
        </Link>
        <div className="flex items-center gap-1 shrink-0">
          <ConnectionStatus />
          {/* Logout was only in the hamburger menu — without this button there
              would be no way to sign out. */}
          <button
            onClick={handleLogout}
            className="p-2 hover:bg-primary-500 rounded-lg"
            aria-label="Logout"
          >
            <LogOut size={22} />
          </button>
        </div>
      </header>

      <OfflineBanner />

      {/* Main content — scrollable. min-h-0 is required: a flex child
          defaults to min-height:auto, which lets it grow to its content
          and shove the bottom nav past the viewport. min-h-0 lets it
          shrink so its own overflow-y-auto scrolls while the nav stays
          pinned to the bottom. */}
      <main className="flex-1 min-h-0 overflow-y-auto p-4">
        <Outlet />
      </main>

      {/* Bottom navigation — Home + actions Home doesn't expose as tiles. */}
      <nav className="flex-shrink-0 bg-white border-t border-gray-200 safe-area-inset-bottom z-10">
        <div className="flex justify-around">
          {bottomNavItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                cn(
                  'flex flex-col items-center py-2 px-5 min-w-16',
                  isActive ? 'text-primary-600' : 'text-gray-500'
                )
              }
            >
              <item.icon size={24} />
              <span className="text-xs mt-1">{item.label}</span>
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  )
}
